import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import MovieCard from "@/components/feature/MovieCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import {
  allMovies,
  getAllGenres,
  getFeatured,
  sortByPopular,
  sortByUpdated,
} from "@/lib/movieData";

const categoryEntries = [
  {
    to: "/category/movie",
    label: "电影专区",
    desc: "最新中文字幕电影，按题材与年份快速筛选",
    icon: "ri-movie-2-line",
    image:
      "https://readdy.ai/api/search-image?query=A%20row%20of%20cinema%20seats%20facing%20a%20glowing%20screen%20in%20a%20dark%20theater%20with%20warm%20ambient%20light%2C%20cinematic%20editorial%20photography%2C%20clean%20composition%2C%20deep%20contrast%2C%20high%20detail%2C%20no%20text&width=900&height=600&seq=zc-cat-movie&orientation=landscape",
  },
  {
    to: "/category/tv",
    label: "电视剧专区",
    desc: "热播剧集中文字幕，剧集列表一目了然",
    icon: "ri-tv-2-line",
    image:
      "https://readdy.ai/api/search-image?query=A%20cozy%20living%20room%20with%20a%20large%20television%20casting%20warm%20light%20in%20the%20evening%2C%20cinematic%20editorial%20photography%2C%20clean%20composition%2C%20deep%20contrast%2C%20high%20detail%2C%20no%20text&width=900&height=600&seq=zc-cat-tv&orientation=landscape",
  },
];

export default function Home() {
  usePageMeta({
    title: "最近中文字幕 - 最新影视资源与中文字幕更新平台",
    description:
      "最近中文字幕网站为用户提供最新影视作品的中文字幕信息和资源，涵盖电影、电视剧等多个类别，帮助用户快速找到想看的带中文字幕内容。",
    keywords:
      "最近中文字幕,中文字幕,最新影视,影视资源,中文字幕电影,中文字幕电视剧,影视更新,字幕下载",
  });

  const featured = getFeatured();
  const latest = sortByUpdated(allMovies).slice(0, 8);
  const popular = sortByPopular(allMovies).slice(0, 8);
  const tags = getAllGenres().slice(0, 12);

  return (
    <div className="w-full">
      <HeroCarousel items={featured} />

      {/* 分类快捷入口 */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {categoryEntries.map((entry) => (
            <Link
              key={entry.to}
              to={entry.to}
              className="group relative overflow-hidden rounded-lg border border-background-200"
            >
              <div className="relative h-52 w-full md:h-64">
                <img
                  src={entry.image}
                  alt={`${entry.label}入口图`}
                  title={entry.label}
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 to-foreground-950/20"></div>
              </div>
              <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-8">
                <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-500 text-background-50 dark:text-foreground-950">
                  <i className={`${entry.icon} text-2xl`}></i>
                </span>
                <h2 className="mt-4 font-heading text-2xl font-bold text-background-50">
                  {entry.label}
                </h2>
                <p className="mt-1 max-w-xs text-sm text-background-200">{entry.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-accent-400">
                  进入专区
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 最新更新 */}
      <section className="w-full px-4 md:px-6 pb-10 md:pb-14">
        <div className="flex items-end justify-between gap-4 mb-5 md:mb-6">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              最新更新
            </h2>
            <p className="mt-1 text-sm text-foreground-500">
              按字幕更新时间倒序，随时掌握最新中文字幕动态
            </p>
          </div>
          <Link
            to="/category/movie"
            className="hidden items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-600 sm:flex whitespace-nowrap"
          >
            查看全部
            <i className="ri-arrow-right-s-line text-lg"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
          {latest.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      {/* 热门推荐 */}
      <section className="w-full bg-background-100 px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-end justify-between gap-4 mb-5 md:mb-6">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              热门推荐
            </h2>
            <p className="mt-1 text-sm text-foreground-500">
              依据观看热度与字幕更新频率综合排序
            </p>
          </div>
          <Link
            to="/search"
            className="hidden items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-600 sm:flex whitespace-nowrap"
          >
            更多资源
            <i className="ri-arrow-right-s-line text-lg"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
          {popular.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      {/* 推荐标签 + 站点简介 */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                关于最近中文字幕
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                最近中文字幕是一个专注影视中文字幕更新信息的展示平台。我们持续整理电影与电视剧的最新字幕动态，
                提供清晰的作品简介、类型标签与字幕说明，帮助你快速找到想看的内容。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                本站所有作品信息均为演示数据，仅用于展示与学习交流，不提供任何在线播放或下载服务。
              </p>
              <Link
                to="/about"
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950 whitespace-nowrap"
              >
                了解我们
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div>
              <h3 className="font-heading text-base font-semibold text-foreground-950 mb-4">
                热门标签
              </h3>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/movie?genre=${encodeURIComponent(tag)}`}
                    className="rounded-full border border-secondary-200 bg-secondary-50 px-3 py-1.5 text-sm text-secondary-900 transition-colors hover:border-accent-300 hover:bg-accent-100"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}